import { useState } from "react";
import { FileUp, Mail } from "lucide-react";
import {
  INTAKE_DOC_LABELS,
  INTAKE_UPLOAD_MAILBOX_ID,
  intakeLinesTotalCents,
  type IntakeAction,
  type IntakeActionKind,
  type IntakeCandidate,
  type IntakeItem,
} from "@clevis/shared-types";
import { Button, Card, Input, Label, Select, cn } from "@clevis/shared-ui";
import { call, CallError } from "../lib/callFunction";
import { useAuth } from "../hooks/useAuth";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { storage } from "../lib/firebase";
import { IntakeCustomerProject, type CustomerProjectChoice } from "./IntakeCustomerProject";
import { useJobTypes } from "../hooks/useJobTypes";
import { useDepartments } from "../hooks/useDepartments";

// One intake item under review: what Clevis thinks the document is, and the
// actions it proposes (create, update, mark done, cancel, note). The reviewer
// keeps, edits or retargets each action, then applies. Shared by the Email
// intake queue and the Work orders page (inline review of an upload).

export type IntakeItemRow = IntakeItem & { id: string };

/** What happened when the reviewer finished with the card. */
export type QueueCardResult =
  | { status: "approved"; applied: IntakeItem["appliedActions"] }
  | { status: "dismissed" };

const DOC_LABEL = INTAKE_DOC_LABELS;
const KIND_LABEL: Record<IntakeActionKind, string> = {
  create_job: "Create job",
  update_job: "Update job",
  complete_job: "Mark done",
  cancel_job: "Cancel job",
  add_note: "Add note",
  create_invoice: "Draft invoice",
  ignore: "No action",
};
// A draft invoice rides with the action that closes its job out (its own
// row, untick to skip); no other row turns into one.
const KINDS = (Object.keys(KIND_LABEL) as IntakeActionKind[]).filter((k) => k !== "create_invoice");
const NEEDS_JOB: IntakeActionKind[] = ["update_job", "complete_job", "cancel_job", "add_note"];
const JOB_STATUS: Record<IntakeCandidate["status"], string> = {
  scheduled: "not started",
  in_progress: "started",
  done: "done",
  canceled: "canceled",
};

type ActionRow = IntakeAction & { include: boolean };

/** The planner's proposal, or — for items queued before it existed — a plain create. */
function startingRows(item: IntakeItemRow): ActionRow[] {
  if (item.actions.length > 0) return item.actions.map((a) => ({ ...a, include: a.kind !== "ignore" }));
  const d = item.draft;
  return [
    {
      id: "a1",
      kind: "create_job",
      jobId: null,
      reason: "Queued before Clevis proposed actions — check it before creating.",
      title: d.title,
      address: d.address ?? "",
      poNumber: d.poNumber,
      lotNumber: d.lotNumber,
      startDate: d.wantedDate,
      note: "",
      lines: [],
      createDone: false,
      forActionId: null,
      jobType: null,
      departmentId: null,
      include: true,
    },
  ];
}

const fmtDay = (ms: number | null) =>
  ms ? new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "no date";
const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export function QueueCard({
  item,
  onResolved,
}: {
  item: IntakeItemRow;
  /** Called after a successful apply or dismiss (the item then leaves the pending list). */
  onResolved?: (result: QueueCardResult) => void;
}) {
  const { tenantId } = useAuth();
  const jobTypes = useJobTypes(tenantId);
  const dept = useDepartments();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState<ActionRow[]>(() => startingRows(item));
  const [editing, setEditing] = useState<string | null>(null);
  // Customer + project (development) for created jobs.
  const [choice, setChoice] = useState<CustomerProjectChoice>({});
  const [ignoreDupes, setIgnoreDupes] = useState(false);

  const d = item.draft;
  const uploaded = item.source.mailboxId === INTAKE_UPLOAD_MAILBOX_ID;
  const arrivedAt = uploaded ? item.createdAt : item.source.receivedAt;
  const candidates = item.candidates ?? [];
  const kept = rows.filter((r) => r.include && r.kind !== "ignore");
  const creating = kept.some((r) => r.kind === "create_job");
  const poClash = kept.filter(
    (r) => r.kind === "create_job" && r.poNumber && candidates.some((c) => c.poNumber === r.poNumber),
  );

  function patch(id: string, next: Partial<ActionRow>) {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...next } : r)));
  }

  /** Point a row at another job; the draft invoice riding on that job moves with it. */
  function retarget(row: ActionRow, jobId: string | null) {
    setRows((rs) =>
      rs.map((r) => (r.id === row.id || (r.kind === "create_invoice" && row.jobId && r.jobId === row.jobId) ? { ...r, jobId } : r)),
    );
  }

  /** Untick the draft invoice billing this row's job (an existing one, or the one this row creates). */
  function untickInvoiceOf(row: ActionRow) {
    setRows((rs) =>
      rs.map((r) =>
        r.kind === "create_invoice" && ((row.jobId && r.jobId === row.jobId) || r.forActionId === row.id)
          ? { ...r, include: false }
          : r,
      ),
    );
  }

  function changeKind(row: ActionRow, kind: IntakeActionKind) {
    // No longer closing out this job (or no longer creating it): its draft invoice has no reason to go out.
    if ((row.jobId && !NEEDS_JOB.includes(kind)) || (row.kind === "create_job" && kind !== "create_job")) {
      untickInvoiceOf(row);
    }
    const next: Partial<ActionRow> = { kind, include: kind !== "ignore" };
    if (NEEDS_JOB.includes(kind)) next.jobId = row.jobId ?? candidates[0]?.id ?? null;
    if (kind === "create_job") {
      const done = d.docType === "completion_notice";
      Object.assign(next, {
        jobId: null,
        title: row.title || d.title,
        address: row.address || d.address || "",
        poNumber: row.poNumber ?? d.poNumber,
        lotNumber: row.lotNumber ?? d.lotNumber,
        startDate: row.startDate ?? (done ? d.completedDate : null) ?? d.wantedDate,
        createDone: done,
      });
    }
    patch(row.id, next);
  }

  async function run(name: "approveIntakeItem" | "dismissIntakeItem") {
    if (!tenantId) return;
    setBusy(true);
    setError("");
    try {
      const res = await call<{ applied?: IntakeItem["appliedActions"] }>(name, {
        tenantId,
        itemId: item.id,
        ...(name === "approveIntakeItem"
          ? {
              actions: kept.map(({ include: _include, ...a }) => a),
              ...(creating ? choice : {}),
              ignoreDupes,
            }
          : {}),
      });
      onResolved?.(name === "approveIntakeItem" ? { status: "approved", applied: res.applied ?? [] } : { status: "dismissed" });
    } catch (err) {
      setError(err instanceof CallError && err.hint ? `${err.message} ${err.hint}` : (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={cn((item.extractError || poClash.length > 0) && "border-brandorange/60")}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          {!item.extractError ? (
            <span className="rounded-full bg-ink px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">
              {DOC_LABEL[d.docType ?? "other"]}
            </span>
          ) : null}
          <span className="font-semibold text-ink">{d.summary || d.title}</span>
          {!item.extractError ? (
            <span
              className={cn(
                "rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase",
                d.confidence === "high" ? "bg-success/15 text-success" : "bg-steel/15 text-steel",
              )}
            >
              {d.confidence}
            </span>
          ) : null}
          {item.matchedPhrases.map((p) => (
            <span key={p} className="rounded-full bg-brandorange/15 px-1.5 py-0.5 text-[10px] text-brandorange-dark">
              “{p}”
            </span>
          ))}
        </div>
        <p className="mt-0.5 truncate text-xs text-steel">
          {uploaded ? <FileUp size={11} className="mr-1 inline" /> : <Mail size={11} className="mr-1 inline" />}
          {item.source.subject} · {uploaded ? `uploaded by ${item.source.from}` : item.source.from}
          {arrivedAt ? ` · ${new Date(arrivedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
        </p>
        {item.portal?.storagePath ? (
          <button
            className="mt-0.5 text-xs text-brandorange-dark underline hover:text-brandorange"
            onClick={() =>
              void getDownloadURL(storageRef(storage, item.portal!.storagePath))
                .then((url) => window.open(url, "_blank", "noopener"))
                .catch((err: Error) => setError(err.message))
            }
          >
            Open the PDF they sent
          </button>
        ) : null}
        {!item.extractError ? (
          <p className="mt-1 text-xs text-ink">
            {[
              d.builderName,
              d.community,
              (d.lots ?? []).length > 0 ? `${d.lots.length} lots` : d.lotNumber ? `Lot ${d.lotNumber}` : "",
              d.poNumber ? `PO ${d.poNumber}` : "",
              d.wantedDate ? `date ${d.wantedDate}` : "",
              d.completedDate ? `completed ${d.completedDate}` : "",
            ]
              .filter(Boolean)
              .join(" · ") || "No structured fields found"}
          </p>
        ) : null}
        {d.lines.length > 0 ? (
          <p className="mt-1 whitespace-pre-line text-xs text-steel">
            {d.lines
              .map(
                (l) =>
                  `${l.quantity} × ${l.description}${l.unitPriceCents !== null ? ` @ $${(l.unitPriceCents / 100).toFixed(2)}` : ""}`,
              )
              .join("\n")}
          </p>
        ) : null}
      </div>

      {item.extractError ? (
        <div className="mt-2 rounded-lg bg-brandorange/10 p-2 text-xs text-ink">
          <strong>Clevis couldn't read this {uploaded ? "file" : "email"}</strong> ({item.extractError}).{" "}
          {uploaded ? "Handle it by hand" : `Open it in ${item.source.mailboxEmail} and handle it by hand`}, then dismiss it
          here.
        </div>
      ) : (
        <div className="mt-3 border-t border-steel/10 pt-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-semibold text-ink">Proposed</span>
            <span className="text-[11px] text-steel">
              {item.planner === "rules"
                ? "AI planner unavailable — proposed by Clevis rules"
                : item.planner === "ai"
                  ? "Proposed by Clevis AI — check before applying"
                  : ""}
            </span>
          </div>
          {rows.map((r) => {
            const target = candidates.find((c) => c.id === r.jobId);
            if (r.kind === "create_invoice") {
              return (
                <div key={r.id} className="border-b border-steel/10 py-1.5 last:border-0">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      aria-label="Create this draft invoice"
                      checked={r.include}
                      onChange={(e) => patch(r.id, { include: e.target.checked })}
                    />
                    <span className="shrink-0 whitespace-nowrap text-xs font-semibold text-ink">
                      Draft invoice · {money(intakeLinesTotalCents(r.lines))}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs text-ink">
                      {r.forActionId
                        ? `for the new job “${rows.find((h) => h.id === r.forActionId)?.title || d.title}”`
                        : target
                          ? `for ${target.title}`
                          : ""}
                    </span>
                  </label>
                  {r.reason ? <p className="ml-6 mt-0.5 text-[11px] text-steel">{r.reason}</p> : null}
                </div>
              );
            }
            return (
              <div key={r.id} className="border-b border-steel/10 py-1.5 last:border-0">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="checkbox"
                    aria-label="Apply this action"
                    checked={r.include && r.kind !== "ignore"}
                    disabled={r.kind === "ignore"}
                    onChange={(e) => {
                      patch(r.id, { include: e.target.checked });
                      if (!e.target.checked && r.kind === "create_job") untickInvoiceOf(r);
                    }}
                  />
                  <Select
                    className="w-32 px-2 py-1 text-xs"
                    value={r.kind}
                    onChange={(e) => changeKind(r, e.target.value as IntakeActionKind)}
                  >
                    {KINDS.map((k) => (
                      <option key={k} value={k} disabled={NEEDS_JOB.includes(k) && candidates.length === 0}>
                        {KIND_LABEL[k]}
                      </option>
                    ))}
                  </Select>
                  {NEEDS_JOB.includes(r.kind) ? (
                    <Select
                      className="min-w-0 flex-1 px-2 py-1 text-xs"
                      value={r.jobId ?? ""}
                      onChange={(e) => retarget(r, e.target.value || null)}
                    >
                      {candidates.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title} · {JOB_STATUS[c.status]} · {fmtDay(c.startAt)} — {c.why}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <span className="min-w-0 flex-1 truncate text-xs text-ink">
                      {r.kind === "create_job" && r.jobType ? <span className="mr-1 rounded bg-steel/10 px-1 text-[10px] text-steel">{r.jobType}</span> : null}
                      {r.kind === "create_job"
                        ? `${r.title || d.title} · ${r.createDone ? `already done${r.startDate ? ` ${r.startDate}` : ""}` : (r.startDate ?? "no date")}${r.address ? ` · ${r.address}` : ""}${r.departmentId ? ` · ${dept.departments.find((x) => x.id === r.departmentId)?.name ?? ""}` : ""}`
                        : ""}
                    </span>
                  )}
                  {r.kind !== "ignore" ? (
                    <button
                      className="shrink-0 text-xs text-steel underline hover:text-ink"
                      onClick={() => setEditing(editing === r.id ? null : r.id)}
                    >
                      {editing === r.id ? "done" : "edit"}
                    </button>
                  ) : null}
                </div>
                {r.reason ? (
                  <p className="ml-6 mt-0.5 text-[11px] text-steel">
                    {r.reason}
                    {target?.status === "in_progress" && r.kind !== "add_note" ? " · this job has already started" : ""}
                  </p>
                ) : null}
                {editing === r.id ? (
                  <div className="ml-6 mt-1 grid grid-cols-2 gap-2">
                    {r.kind === "create_job" || r.kind === "update_job" ? (
                      <>
                        <div className="col-span-2">
                          <Label>{r.kind === "update_job" ? "New title (blank = keep)" : "Job title"}</Label>
                          <Input value={r.title} onChange={(e) => patch(r.id, { title: e.target.value })} />
                        </div>
                        <div>
                          <Label>
                            {r.kind === "update_job" ? "New date (blank = keep)" : r.createDone ? "Completed on" : "Scheduled date"}
                          </Label>
                          <Input
                            type="date"
                            value={r.startDate ?? ""}
                            onChange={(e) => patch(r.id, { startDate: e.target.value || null })}
                          />
                        </div>
                        <div>
                          <Label>{r.kind === "update_job" ? "New address (blank = keep)" : "Address"}</Label>
                          <Input value={r.address} onChange={(e) => patch(r.id, { address: e.target.value })} />
                        </div>
                      </>
                    ) : null}
                    {r.kind === "create_job" ? (
                      <label className="col-span-2 flex cursor-pointer items-center gap-2 text-xs text-ink">
                        <input
                          type="checkbox"
                          checked={r.createDone}
                          onChange={(e) => {
                            patch(r.id, { createDone: e.target.checked });
                            if (!e.target.checked) untickInvoiceOf(r);
                          }}
                        />
                        Already done (the builder reported this work complete)
                      </label>
                    ) : null}
                    {r.kind === "create_job" ? (
                      <div>
                        <Label>Job type</Label>
                        <Select className="px-2 py-1 text-xs" value={r.jobType ?? ""} onChange={(e) => patch(r.id, { jobType: e.target.value || null })}>
                          <option value="">No type</option>
                          {[...new Set([...jobTypes, ...(r.jobType ? [r.jobType] : [])])].map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    {r.kind === "create_job" && dept.departments.length > 0 ? (
                      <div>
                        <Label>Department</Label>
                        <Select className="px-2 py-1 text-xs" value={r.departmentId ?? ""} onChange={(e) => patch(r.id, { departmentId: e.target.value || null })}>
                          <option value="">No department</option>
                          {dept.departments.map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.name}
                            </option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    {r.kind === "create_job" ? (
                      <div>
                        <Label>PO number</Label>
                        <Input
                          value={r.poNumber ?? ""}
                          onChange={(e) => patch(r.id, { poNumber: e.target.value.trim() || null })}
                        />
                      </div>
                    ) : null}
                    <div className={r.kind === "create_job" ? "" : "col-span-2"}>
                      <Label>Note for the job</Label>
                      <Input value={r.note} onChange={(e) => patch(r.id, { note: e.target.value })} />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}

          {creating && tenantId ? (
            <IntakeCustomerProject
              tenantId={tenantId}
              itemId={item.id}
              matches={item.dupes.customers}
              builderName={d.builderName}
              community={d.community}
              hints={[d.title, d.summary, d.scope, ...d.lines.map((l) => l.description)].filter(Boolean).join(" ")}
              newJobs={kept.filter((r) => r.kind === "create_job").length}
              onChange={setChoice}
            />
          ) : null}

          {poClash.length > 0 ? (
            <label className="mt-2 flex cursor-pointer items-center gap-2 rounded-lg bg-brandorange/10 p-2 text-xs text-ink">
              <input type="checkbox" checked={ignoreDupes} onChange={(e) => setIgnoreDupes(e.target.checked)} />
              PO {poClash.map((r) => r.poNumber).join(", ")} is already on an existing job. Create a second job anyway
            </label>
          ) : null}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!item.extractError ? (
          <Button
            className="px-3 py-1 text-xs"
            busy={busy}
            disabled={kept.length === 0 || (poClash.length > 0 && !ignoreDupes)}
            onClick={() => void run("approveIntakeItem")}
          >
            {kept.length === 0 ? "Nothing to apply" : `Apply ${kept.length} action${kept.length > 1 ? "s" : ""}`}
          </Button>
        ) : null}
        <Button variant="ghost" className="px-2 py-1 text-xs" busy={busy} onClick={() => void run("dismissIntakeItem")}>
          Dismiss
        </Button>
        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </div>
    </Card>
  );
}
